// Two separate namespaces: the receiver page is an SPA route, JUTE's bundle files are service-worker
// territory. Sharing one prefix made a worker miss indistinguishable from a route, and the SPA
// fallback served Designer inside the preview frame with HTTP 200. See PROJECT_WEBRTC_REMOTE_PHASES.md.
// No imports — src/index.tsx consults this during boot, before anything else loads.

/** The versioned asset directory (`/static/` locally, `/<pipelineId>/` deployed) — the only directory
 *  the deploy pipeline uploads, so everything the workers touch must hang off it. */
export const PREVIEW_ASSET_ROOT = `/${__STATIC_PATH__}/`;

/** The page the QR points at. An SPA route; deliberately NOT covered by the worker's scope. */
export const RECEIVER_PATH = '/__remote-preview__/';

/** The desktop pop-out shell — hosts the JUTE iframe plus the synthetic camera. Also an SPA route, and
 *  also outside the worker's scope: it must be able to read Cache Storage as an UNcontrolled client. */
export const LOCAL_PREVIEW_PATH = '/__local-preview__/';

/** JUTE's bundle files, one directory per session — the worker's scope, never an SPA route. Must stay
 *  under the asset root: a worker's scope is capped by where its script is served from. */
export const JUTE_PATH_PREFIX = `${PREVIEW_ASSET_ROOT}__remote-jute__/`;

/** The worker's script URL, derived from the same root as its scope so the two cannot drift. */
export const REMOTE_PREVIEW_SW_URL = `${PREVIEW_ASSET_ROOT}remote-preview-sw.js`;

export type RemotePreviewRoute =
    /** The receiver page itself — render RemotePreviewReceiver. */
    | 'receiver'
    /** The desktop pop-out shell — render LocalPreviewShell. */
    | 'local-preview'
    /** A path only the worker should answer. Reaching the app means the worker isn't controlling its
     *  scope — a hard error, never a Designer boot. */
    | 'sw-miss'
    /** Everything else — the Designer app. */
    | 'app';

/** Classify a pathname. Tolerates a duplicated leading slash, which used to fall through to the
 *  Designer boot and send the phone to an OAuth login page. */
export function matchRemotePreviewRoute(pathname: string): RemotePreviewRoute {
    const path = pathname.replace(/^\/+/, '/');

    if (path === RECEIVER_PATH || path === RECEIVER_PATH.slice(0, -1)) return 'receiver';
    if (path === LOCAL_PREVIEW_PATH || path === LOCAL_PREVIEW_PATH.slice(0, -1)) return 'local-preview';
    if (path.startsWith(JUTE_PATH_PREFIX)) return 'sw-miss';
    // Nothing lives deeper than either shell page, so a deeper path is stale or mangled.
    if (path.startsWith(RECEIVER_PATH)) return 'sw-miss';
    if (path.startsWith(LOCAL_PREVIEW_PATH)) return 'sw-miss';

    return 'app';
}

/** The trailing slash is REQUIRED: JUTE resolves `content.json` and `fs/<id>` off
 *  `window.location.pathname`, and the worker maps the trailing slash onto `index.html`. */
export function jutePathFor(remoteSessionId: string): string {
    return `${JUTE_PATH_PREFIX}${remoteSessionId}/`;
}

/** True when this host exists only to serve previews: `_env.WEBRTC_PREVIEW_ORIGIN` names an origin and
 *  we are on it. Origins are compared whole, so a scheme or port mismatch is never quietly accepted.
 *  Unset means "not split" — the shape of the rollback, and of every environment before Phase 3.
 *
 *  Takes the raw configured value rather than a normalised one: `@/settings` cannot be imported here. */
export function isPreviewOnlyOrigin(origin: string, previewOrigin: string): boolean {
    if (!previewOrigin) return false;
    try {
        return new URL(previewOrigin).origin === origin;
    } catch {
        return false;
    }
}

/** The Designer shell has nothing to do on the preview host, so it renders a stub instead of booting. */
export type BootVerdict = RemotePreviewRoute | 'preview-stub';

/** Folds the origin into the route verdict. Split out of src/index.tsx so the rule that matters is
 *  testable without React: `'sw-miss'` must survive. A preview host that answered it with a friendly
 *  stub would hide the exact failure that verdict exists to expose. */
export function resolveBootVerdict(route: RemotePreviewRoute, previewOnlyOrigin: boolean): BootVerdict {
    if (!previewOnlyOrigin) return route;
    return route === 'app' || route === 'local-preview' ? 'preview-stub' : route;
}
